import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Backdrop,
  Snackbar,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalance as BankIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';

// Entity Types as per specification
const entityTypes = [
  { id: 'vendor', name: 'Vendor', description: 'External supplier or vendor' },
  { id: 'partner', name: 'Partner', description: 'Business partner' },
  { id: 'company', name: 'Company', description: 'Corporate entity' },
];

// Payment Methods as per specification
const paymentMethods = [
  { id: 'bank_transfer', name: 'Bank Transfer' },
  { id: 'paypal', name: 'PayPal' },
  { id: 'wise', name: 'Wise' },
  { id: 'crypto', name: 'Crypto' },
  { id: 'other', name: 'Other' },
];

const generateVendorPortalToken = () => {
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (_) {
    return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  }
};

// Entity types that can have a UMA Vendor Page. Partner pages also roll up
// their sub-accounts.
const VENDOR_PAGE_TYPES = ['vendor', 'partner'];
const supportsVendorPage = (entityType) => VENDOR_PAGE_TYPES.includes(entityType);

const buildVendorPortalLink = (entityId, token) => {
  if (!entityId || !token) return '';
  const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  const base = origin || '';
  return `${base}/v/${encodeURIComponent(entityId)}/${encodeURIComponent(token)}`;
};

// Entity Status options (blacklisted removed from selectable options)
const entityStatuses = [
  { id: 'active', name: 'Active' },
  { id: 'inactive', name: 'Inactive' },
];

// Creation should only allow Active by default
const entityStatusesForCreate = [
  { id: 'active', name: 'Active' },
];

// Tags are now loaded dynamically from App Config

export default function Entities() {
  const navigate = useNavigate();
  const { getCountryOptions, getCurrencyOptions, getEntityTagOptions, loading: configLoading } = useConfig();
  const { isSuperAdmin, isMember } = useAuth();

  // Get dynamic configuration options
  const countries = getCountryOptions();
  const currencies = getCurrencyOptions();
  const entityTags = getEntityTagOptions();

  const [entities, setEntities] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tagsSelectOpen, setTagsSelectOpen] = useState(false);
  const [filters, setFilters] = useState({
    entityType: '',
    country: '',
    status: 'active',
  });
  const [formData, setFormData] = useState({
    entityName: '',
    entityType: '',
    country: '',
    taxId: '',
    email: '',
    phone: '',
    notes: '',
    tags: [], // Multi-select tags for filtering and reports
    status: 'active',
    accountType: '', // Required field: 'direct' or 'sub'
    parentAccountId: '', // For sub accounts only
    parentEntityId: '', // For employees (always required) and optional for others
    bankingDetails: {
      bankName: '',
      accountHolderName: '',
      iban: '',
      swift: '',
      paymentMethod: 'bank_transfer',
      preferredCurrency: 'USD',
      bankCountry: '',
    },
    vendorPortalEnabled: false,
    vendorPortalToken: '',
  });

  const copyLinkToClipboard = async (link) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setSnackbar({ open: true, message: 'Vendor portal link copied to clipboard', severity: 'success' });
    } catch (_) {
      window.prompt('Copy this link', link);
      setSnackbar({ open: true, message: 'Copy this vendor portal link manually', severity: 'info' });
    }
  };

  const getVendorPortalLink = (entity) => {
    if (!entity || !supportsVendorPage(entity.entityType) || !entity.vendorPortalEnabled || !entity.vendorPortalToken) {
      return '';
    }
    return buildVendorPortalLink(entity.id, entity.vendorPortalToken);
  };

  const handleToggleVendorPortal = async (entity) => {
    if (!entity || !entity.id) return;
    if (!supportsVendorPage(entity.entityType)) {
      setSnackbar({ open: true, message: 'UMA Vendor Page is available for vendor and partner entities only', severity: 'warning' });
      return;
    }

    const isDirectAccount = !entity.accountType || entity.accountType === 'direct';
    if (!isDirectAccount && !entity.vendorPortalEnabled) {
      setSnackbar({ open: true, message: 'Vendor portal requires a Direct Account', severity: 'warning' });
      return;
    }

    const enable = !entity.vendorPortalEnabled;
    const payload = {
      vendorPortalEnabled: enable,
      ...(enable
        ? { vendorPortalEnabledAt: serverTimestamp(), vendorPortalDisabledAt: null }
        : { vendorPortalDisabledAt: serverTimestamp() }),
    };

    if (enable && !entity.vendorPortalToken) {
      payload.vendorPortalToken = generateVendorPortalToken();
    }

    try {
      setSaving(true);
      const ref = doc(db, 'entities', entity.id);
      await updateDoc(ref, payload);
      setSnackbar({ open: true, message: enable ? 'Vendor portal enabled' : 'Vendor portal disabled', severity: 'success' });
    } catch (error) {
      console.error('Error toggling vendor portal:', error);
      setSnackbar({ open: true, message: 'Failed to update vendor portal setting', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyVendorPortalLink = async (entity, options = {}) => {
    if (!entity || !entity.id) return;
    const { tokenOverride, skipPersist = false } = options;
    if (!supportsVendorPage(entity.entityType) && !tokenOverride) {
      setSnackbar({ open: true, message: 'Vendor portal link is only available for vendor and partner entities', severity: 'warning' });
      return;
    }
    const isEnabled = tokenOverride ? true : Boolean(entity.vendorPortalEnabled);
    if (!isEnabled) {
      setSnackbar({ open: true, message: 'Enable the vendor portal to copy the link', severity: 'warning' });
      return;
    }

    let token = tokenOverride || entity.vendorPortalToken;
    let shouldUnsetSaving = false;

    try {
      if (!token) {
        token = generateVendorPortalToken();
        if (!skipPersist) {
          setSaving(true);
          shouldUnsetSaving = true;
          const ref = doc(db, 'entities', entity.id);
          await updateDoc(ref, {
            vendorPortalToken: token,
            vendorPortalEnabled: true,
            vendorPortalEnabledAt: serverTimestamp(),
          });
        }
      }

      const link = buildVendorPortalLink(entity.id, token);
      await copyLinkToClipboard(link);
    } catch (error) {
      console.error('Error copying vendor portal link:', error);
      setSnackbar({ open: true, message: 'Could not prepare vendor portal link', severity: 'error' });
    } finally {
      if (shouldUnsetSaving) {
        setSaving(false);
      }
    }
  };

  // Firestore operations
  const fetchEntities = async () => {
    try {
      setLoading(true);
      const entitiesRef = collection(db, 'entities');
      const q = query(entitiesRef, orderBy('entityName', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const entitiesData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          entitiesData.push({
            id: doc.id,
            ...data,
            tags: data.tags || [],
            parentEntityId: data.parentEntityId || '',
            bankingDetails: data.bankingDetails || {
              bankName: '',
              accountHolderName: '',
              iban: '',
              swift: '',
              paymentMethod: 'bank_transfer',
              preferredCurrency: 'USD',
              bankCountry: '',
            },
          });
        });
        setEntities(entitiesData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching entities:', error);
      setSnackbar({ open: true, message: 'Error loading entities', severity: 'error' });
      setLoading(false);
    }
  };

  const addEntity = async (entityData) => {
    try {
      setSaving(true);
      
      // Check for duplicate entity name
      const existingEntity = entities.find(e => 
        e.entityName.toLowerCase() === entityData.entityName.toLowerCase()
      );
      if (existingEntity) {
        throw new Error('An entity with this name already exists');
      }

      const docRef = await addDoc(collection(db, 'entities'), {
        ...entityData,
        bankingDetails: {
          ...entityData.bankingDetails,
        },
        createdAt: serverTimestamp(),
        modifiedAt: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: 'Entity created successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding entity:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Error creating entity', 
        severity: 'error' 
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateEntity = async (entityId, entityData) => {
    try {
      setSaving(true);
      
      // Check for duplicate entity name (excluding current entity)
      const existingEntity = entities.find(e => 
        e.id !== entityId && 
        e.entityName.toLowerCase() === entityData.entityName.toLowerCase()
      );
      if (existingEntity) {
        throw new Error('An entity with this name already exists');
      }

      const entityRef = doc(db, 'entities', entityId);
      await updateDoc(entityRef, {
        ...entityData,
        modifiedAt: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: 'Entity updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating entity:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Error updating entity', 
        severity: 'error' 
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Filter entities based on current filters
  useEffect(() => {
    let filtered = [...entities];

    if (filters.entityType) {
      filtered = filtered.filter(entity => entity.entityType === filters.entityType);
    }
    if (filters.country) {
      filtered = filtered.filter(entity => entity.country === filters.country);
    }
    if (filters.status) {
      filtered = filtered.filter(entity => entity.status === filters.status);
    }

    setFilteredEntities(filtered);
  }, [entities, filters]);

  // Load entities on component mount
  useEffect(() => {
    const unsubscribe = fetchEntities();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // Form handlers
  const handleOpenDialog = (entity = null) => {
    if (entity) {
      setEditingEntity(entity);
      setFormData({
          entityName: entity.entityName || '',
          entityType: entity.entityType || 'vendor',
          country: entity.country || '',
          taxId: entity.taxId || '',
          email: entity.email || '',
          phone: entity.phone || '',
          notes: entity.notes || '',
          tags: entity.tags || [],
          // Coerce any legacy/unknown status (e.g., blacklisted) to inactive in edit mode
          status: ['active', 'inactive'].includes(entity.status) ? entity.status : 'inactive',
          accountType: entity.entityType === 'employee' ? 'sub' : (entity.accountType || ''),
          parentAccountId: entity.parentAccountId || entity.parentEntityId || '',
          parentEntityId: entity.parentEntityId || entity.parentAccountId || '',
          bankingDetails: entity.bankingDetails || {
            bankName: '',
            accountHolderName: '',
            iban: '',
            swift: '',
            paymentMethod: 'bank_transfer',
            preferredCurrency: 'USD',
            bankCountry: '',
          },
          vendorPortalEnabled: Boolean(entity.vendorPortalEnabled),
          vendorPortalToken: entity.vendorPortalToken || '',
        });
    } else {
      setEditingEntity(null);
      setFormData({
          entityName: '',
          entityType: '',
          country: '',
          taxId: '',
          email: '',
          phone: '',
          notes: '',
          tags: [],
          status: 'active',
          accountType: '',
          parentAccountId: '',
          parentEntityId: '',
          bankingDetails: {
            bankName: '',
            accountHolderName: '',
            iban: '',
            swift: '',
            paymentMethod: 'bank_transfer',
            preferredCurrency: 'USD',
            bankCountry: '',
          },
          vendorPortalEnabled: false,
          vendorPortalToken: '',
        });
    }
    setOpenDialog(true);
    setTagsSelectOpen(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEntity(null);
    setTagsSelectOpen(false);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.entityName.trim()) {
        setSnackbar({ open: true, message: 'Entity name is required', severity: 'error' });
        return;
      }
      if (!formData.entityType) {
        setSnackbar({ open: true, message: 'Entity type is required', severity: 'error' });
        return;
      }
      if (!formData.country) {
        setSnackbar({ open: true, message: 'Country is required', severity: 'error' });
        return;
      }
      if (formData.entityType !== 'employee' && !formData.accountType) {
        setSnackbar({ open: true, message: 'Account Type is required', severity: 'error' });
        return;
      }
      if (formData.accountType === 'sub' && !formData.parentAccountId) {
        setSnackbar({ open: true, message: 'Parent Account is required for Sub Accounts', severity: 'error' });
        return;
      }

      // Business Rules Enforcement (new rules)
      const submissionData = { ...formData };

      // Partners and Companies are always direct
      if (submissionData.entityType === 'partner' || submissionData.entityType === 'company') {
        submissionData.accountType = 'direct';
        submissionData.parentAccountId = '';
      }

      // Employees are always sub-accounts and must have a parent account (Partner or Company)
      if (submissionData.entityType === 'employee') {
        submissionData.accountType = 'sub';
        if (!submissionData.parentAccountId) {
          setSnackbar({ open: true, message: 'Employees must have a parent account (Partner or Company)', severity: 'error' });
          return;
        }
        const parentEntity = entities.find(e => e.id === submissionData.parentAccountId);
        if (!parentEntity || !['partner', 'company'].includes(parentEntity.entityType)) {
          setSnackbar({ open: true, message: 'Employee parent must be a Partner or Company direct account', severity: 'error' });
          return;
        }
        // Keep parentEntityId in sync for display/compatibility
        submissionData.parentEntityId = submissionData.parentAccountId;
      }

      if (!supportsVendorPage(submissionData.entityType) || submissionData.accountType !== 'direct') {
        submissionData.vendorPortalEnabled = false;
      }

      if (submissionData.vendorPortalEnabled && !submissionData.vendorPortalToken) {
        submissionData.vendorPortalToken = generateVendorPortalToken();
      }

      if (submissionData.vendorPortalEnabled) {
        if (!editingEntity || !editingEntity.vendorPortalEnabled) {
          submissionData.vendorPortalEnabledAt = serverTimestamp();
          submissionData.vendorPortalDisabledAt = null;
        }
      } else if (editingEntity?.vendorPortalEnabled) {
        submissionData.vendorPortalDisabledAt = serverTimestamp();
      }

      if (editingEntity) {
        await updateEntity(editingEntity.id, submissionData);
      } else {
        await addEntity(submissionData);
      }
      
      handleCloseDialog();
    } catch (error) {
      // Error is handled in the add/update functions
    }
  };

  const handleCountryChange = (countryCode) => {
    setFormData(prev => ({
      ...prev,
      country: countryCode,
    }));
  };

  const getEntityTypeName = (type) => {
    const entityType = entityTypes.find(t => t.id === type);
    return entityType ? entityType.name : type;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'blacklisted': return 'error';
      default: return 'default';
    }
  };

  const getCountryName = (code) => {
    const country = countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const getParentEntityName = (parentEntityId) => {
    if (!parentEntityId) return null;
    const parentEntity = entities.find(e => e.id === parentEntityId);
    return parentEntity ? parentEntity.entityName : 'Unknown';
  };

  // Get eligible parent accounts for sub accounts
  const getEligibleParentAccounts = () => {
    return entities.filter(entity => {
      // Restrict employees' parents to Partner or Company; others can be Vendor, Partner, or Company
      const validTypes = formData.entityType === 'employee' ? ['partner', 'company'] : ['vendor', 'partner', 'company'];
      if (!validTypes.includes(entity.entityType)) return false;
      
      // Must be a direct account
      if (entity.accountType !== 'direct') return false;
      
      // Must be active
      if (entity.status !== 'active') return false;
      
      // Cannot be the current entity (when editing)
      if (editingEntity && entity.id === editingEntity.id) return false;
      
      return true;
    });
  };

  // Get eligible parent entities for employees (Partner or Company only)
  const getEligibleParentEntities = () => {
    return entities.filter(entity => {
      // Must be Partner or Company
      const validTypes = ['partner', 'company'];
      if (!validTypes.includes(entity.entityType)) return false;
      
      // Must be active
      if (entity.status !== 'active') return false;
      
      // Cannot be the current entity (when editing)
      if (editingEntity && entity.id === editingEntity.id) return false;
      
      return true;
    });
  };

  if (configLoading || loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  const isAdmin = isSuperAdmin();
  // Members don't see the External URLs column
  const showExternalUrls = !isMember();

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Entities
            </Typography>

          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Entity
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    value={filters.entityType}
                    label="Entity Type"
                    onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {entityTypes.map(type => (
                      <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={filters.country}
                    label="Country"
                    onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                  >
                    <MenuItem value="">All Countries</MenuItem>
                    {countries.map(country => (
                      <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {entityStatuses.map(status => (
                      <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
      </Card>

      {/* Entities Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Entity Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Account Type</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Contact</TableCell>
                {showExternalUrls && <TableCell>External URLs</TableCell>}
                {isAdmin && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6 + (showExternalUrls ? 1 : 0) + (isAdmin ? 1 : 0)} align="center">
                    <Box sx={{ py: 4 }}>
                      <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No entities found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {entities.length === 0 
                          ? 'Create your first entity to get started'
                          : 'Try adjusting your filters'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntities.map((entity) => {
                  const vendorPortalLink = getVendorPortalLink(entity);
                  const canCreateVendorPortalLink = isAdmin && supportsVendorPage(entity.entityType) && entity.vendorPortalEnabled;

                  return (
                    <TableRow key={entity.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {entity.entityName}
                          </Typography>
                          {entity.entityType === 'employee' && entity.parentEntityId && (
                            <Typography variant="caption" color="text.secondary">
                              Parent: {getParentEntityName(entity.parentEntityId)}
                            </Typography>
                          )}
                          {entity.taxId && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Tax ID: {entity.taxId}
                            </Typography>
                          )}
                          {entity.tags && entity.tags.length > 0 && (
                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {entity.tags.slice(0, 2).map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                              ))}
                              {entity.tags.length > 2 && (
                                <Chip 
                                  label={`+${entity.tags.length - 2} more`} 
                                  size="small" 
                                  variant="outlined"
                                  
                                />
                              )}
                            </Box>
                          )}
                          {/* {entity.vendorPortalEnabled && entity.entityType === 'vendor' && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                icon={<LinkIcon fontSize="small" />}
                                label="Vendor Portal On"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          )} */}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entity.status} 
                          size="small"
                          color={getStatusColor(entity.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getEntityTypeName(entity.entityType)} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {entity.entityType === 'employee' ? (
                          <Chip 
                            label="Employee" 
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        ) : entity.accountType ? (
                          <Box>
                            <Chip 
                              label={entity.accountType === 'direct' ? 'Direct Account' : 'Sub Account'}
                              size="small"
                              color={entity.accountType === 'sub' ? 'warning' : 'secondary'}
                              variant="outlined"
                            />
                            {entity.accountType === 'sub' && entity.parentAccountId && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                Parent: {entities.find(e => e.id === entity.parentAccountId)?.entityName || 'Unknown'}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Chip 
                            label="Not Set" 
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>{getCountryName(entity.country)}</TableCell>
                      <TableCell>
                        <Box>
                          {entity.email && (
                            <Typography variant="caption" display="block">
                              📧 {entity.email}
                            </Typography>
                          )}
                          {entity.phone && (
                            <Typography variant="caption" display="block">
                              📞 {entity.phone}
                            </Typography>
                          )}
                          {!entity.email && !entity.phone && (
                            <Typography variant="caption" color="text.secondary">
                              No contact info
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {showExternalUrls && <TableCell>
                        {vendorPortalLink ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Button
                              component="a"
                              href={vendorPortalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              startIcon={<LinkIcon />}
                            >
                              Vendor Ledger
                            </Button>
                            <Tooltip title="Copy vendor ledger link">
                              <IconButton
                                size="small"
                                onClick={() => copyLinkToClipboard(vendorPortalLink)}
                                color="primary"
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : canCreateVendorPortalLink ? (
                          <Button
                            size="small"
                            startIcon={<LinkIcon />}
                            onClick={() => handleCopyVendorPortalLink(entity)}
                          >
                            Create Link
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>}
                      {isAdmin && (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {supportsVendorPage(entity.entityType) && (
                              <Tooltip title={entity.vendorPortalEnabled ? 'Disable UMA Vendor Page' : 'Enable UMA Vendor Page'}>
                                <Switch
                                  size="small"
                                  checked={Boolean(entity.vendorPortalEnabled)}
                                  onChange={() => handleToggleVendorPortal(entity)}
                                  inputProps={{ 'aria-label': 'toggle vendor portal' }}
                                />
                              </Tooltip>
                            )}
                            <Tooltip title="Edit entity">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenDialog(entity)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Entity Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingEntity ? 'Edit Entity' : 'Add New Entity'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Entity Name"
                value={formData.entityName}
                onChange={(e) => setFormData({ ...formData, entityName: e.target.value })}
                required
                helperText="Unique name for this entity"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={formData.entityType}
                  label="Entity Type"
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData({
                      ...formData,
                      entityType: newType,
                      accountType:
                        newType === 'employee'
                          ? 'sub'
                          : (['partner', 'company'].includes(newType) ? 'direct' : formData.accountType),
                      parentAccountId: ['employee', 'partner', 'company'].includes(newType) ? '' : formData.parentAccountId,
                    });
                  }}
                  disabled={editingEntity} // Type Immutability: Cannot change type after creation
                >
                  <MenuItem value="">Select Entity Type</MenuItem>
                  {entityTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        - {type.description}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
                {editingEntity && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                    Entity type cannot be changed once created
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Employees pick Parent Account below (always sub-account) */}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  value={formData.country}
                  label="Country"
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  {countries.map(country => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  {(editingEntity ? entityStatuses : entityStatusesForCreate).map(status => (
                    <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* Payments Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Payments
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tax ID / VAT"
                value={formData.taxId}
                onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SecurityIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Required for taxable entities"
              />
            </Grid>
            {/* Account Type - Not applicable for Employees (fixed to Sub Account) */}
            {formData.entityType !== '' && formData.entityType !== 'employee' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={formData.accountType}
                    label="Account Type"
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        accountType: e.target.value,
                        parentAccountId: e.target.value === 'direct' ? '' : formData.parentAccountId
                      });
                    }}
                    disabled={(editingEntity && editingEntity.accountType) || ['partner','company'].includes(formData.entityType)} // Cannot change once set; fixed for partner/company
                  >
                    <MenuItem value="">Select Account Type</MenuItem>
                    <MenuItem value="direct">
                      Direct Account
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        - Real account, shows in Accounts Payable
                      </Typography>
                    </MenuItem>
                    <MenuItem value="sub">
                      Sub Account
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        - Balance tracked under parent account
                      </Typography>
                    </MenuItem>
                  </Select>
                  {editingEntity && editingEntity.accountType && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                      Account type cannot be changed once set
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}
            {formData.entityType === 'employee' && (
              <Grid item xs={12} md={6}>
                <TextField fullWidth value="Sub Account" label="Account Type" disabled />
              </Grid>
            )}
            {formData.accountType === 'sub' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Parent Account</InputLabel>
                  <Select
                    value={formData.parentAccountId}
                    label="Parent Account"
                    onChange={(e) => setFormData({ ...formData, parentAccountId: e.target.value })}
                  >
                    <MenuItem value="">Select Parent Account</MenuItem>
                    {getEligibleParentAccounts().map(entity => (
                      <MenuItem key={entity.id} value={entity.id}>
                        {entity.entityName}
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          - {getEntityTypeName(entity.entityType)}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Banking Details - Only shown for Direct Accounts (not for Employees) */}
            {formData.entityType !== '' && formData.entityType !== 'employee' && formData.accountType === 'direct' && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BankIcon />
                      <Typography variant="h6">Banking Details</Typography>
                      <Typography variant="caption" color="text.secondary">
                        (Optional but recommended for payments)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Bank Name"
                          value={formData.bankingDetails.bankName}
                          onChange={(e) => setFormData({
                            ...formData,
                            bankingDetails: {
                              ...formData.bankingDetails,
                              bankName: e.target.value
                            }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Account Holder Name"
                          value={formData.bankingDetails.accountHolderName}
                          onChange={(e) => setFormData({
                            ...formData,
                            bankingDetails: {
                              ...formData.bankingDetails,
                              accountHolderName: e.target.value
                            }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="IBAN / Account Number"
                          value={formData.bankingDetails.iban}
                          onChange={(e) => setFormData({
                            ...formData,
                            bankingDetails: {
                              ...formData.bankingDetails,
                              iban: e.target.value
                            }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="SWIFT / Routing Number"
                          value={formData.bankingDetails.swift}
                          onChange={(e) => setFormData({
                            ...formData,
                            bankingDetails: {
                              ...formData.bankingDetails,
                              swift: e.target.value
                            }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Payment Method</InputLabel>
                          <Select
                            value={formData.bankingDetails.paymentMethod}
                            label="Payment Method"
                            onChange={(e) => setFormData({
                              ...formData,
                              bankingDetails: {
                                ...formData.bankingDetails,
                                paymentMethod: e.target.value
                              }
                            })}
                          >
                            {paymentMethods.map(method => (
                              <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Bank Country</InputLabel>
                          <Select
                            value={formData.bankingDetails.bankCountry}
                            label="Bank Country"
                            onChange={(e) => setFormData({
                              ...formData,
                              bankingDetails: {
                                ...formData.bankingDetails,
                                bankCountry: e.target.value
                              }
                            })}
                          >
                            {countries.map(country => (
                              <MenuItem key={country.code} value={country.code}>
                                {country.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            {supportsVendorPage(formData.entityType) && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  UMA Vendor Page
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="medium"
                        checked={formData.vendorPortalEnabled}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            vendorPortalEnabled: enabled,
                            vendorPortalToken: enabled ? (prev.vendorPortalToken || generateVendorPortalToken()) : prev.vendorPortalToken,
                          }));
                        }}
                        disabled={formData.accountType !== 'direct' && !formData.vendorPortalEnabled}
                      />
                    }
                    label={`Allow this ${formData.entityType === 'partner' ? 'partner' : 'vendor'} to view their UMA history${formData.entityType === 'partner' ? ', including sub-accounts' : ''}`}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Share a read-only page showing ledger history and the current balance owed.
                  </Typography>
                  {formData.accountType !== 'direct' && !formData.vendorPortalEnabled && (
                    <Alert severity="info">Vendor portal requires the account to be set as Direct.</Alert>
                  )}
                  {editingEntity && formData.vendorPortalEnabled && formData.vendorPortalToken && (
                    <TextField
                      fullWidth
                      label="Vendor Portal Link"
                      value={buildVendorPortalLink(editingEntity.id, formData.vendorPortalToken)}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy link">
                              <IconButton
                                edge="end"
                                onClick={() => handleCopyVendorPortalLink(editingEntity, { tokenOverride: formData.vendorPortalToken, skipPersist: true })}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Box>
              </Grid>
            )}

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                helperText="Additional context or special instructions"
              />
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  open={tagsSelectOpen}
                  onOpen={() => setTagsSelectOpen(true)}
                  onClose={() => setTagsSelectOpen(false)}
                  value={formData.tags}
                  label="Tags"
                  onChange={(e) => {
                    setFormData({ ...formData, tags: e.target.value });
                    setTagsSelectOpen(false); // Close immediately after selection
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {entityTags.map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select tags for filtering and reports
                </Typography>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : (editingEntity ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop open={saving} sx={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
